import React from 'react';

import {
  IconChevronRight,
  IllustrationPersonWithFlag,
  CardSubSection,
  IconInfoRoundFilled,
} from '@maiia/design-system';
import {
  Alert,
  Box,
  Card,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCurrentUser } from '@docavenue/components';
import { useRouter } from 'next/router';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { Trans, useTranslation } from '@/src/i18n';
import IconModalIndication from '../IconModalIndication/IconModalIndication';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import { APP_ROUTES } from '@/src/constants';
import useRemainingCostInsuranceContract from '@/src/hooks/insurances/useRemainingCostInsuranceContract';

type Props = {
  card: CardHcd;
};

// 13/04/2023: added label prop just for information, not used for now
const codeList = [
  { code: '70', label: 'Masseur-kinésithérapeute' },
  { code: 'SM44', label: 'Radiologue' },
  { code: 'SM15', label: 'Dermatologue et vénérologue' },
  { code: 'SM24', label: 'Gastro-entérologue et hépatologue' },
  { code: 'SM04', label: 'Cardiologue' },
  { code: 'SM48', label: 'Rhumatologue' },
  { code: 'SM16', label: 'Endocrinologue et Métabolisme' },
  { code: '92', label: 'Orthoptiste' },
  { code: 'SM36', label: 'Oncologue médical' },
  { code: 'SM41', label: 'Pneumologue' },
  { code: 'SM62', label: 'Endocrinologue-Diabétologue-Nutrition' },
  { code: 'SM39', label: 'Oto-Rhino-Laryngologiste' },
  {
    code: 'SM34',
    label: 'Oto-Rhino-Laryngologiste et Chirurgien cervico-facial',
  },
  { code: 'SM57', label: 'Allergologue' },
  { code: 'C34', label: 'Angiologue' },
  { code: '91', label: 'Orthophoniste' },
  { code: '60', label: 'Infirmier' },
  { code: '80', label: 'Pédicure-podologue' },
];

const Refunds = ({ card }: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const theme = useTheme();
  const currentUser = useCurrentUser();
  const { isFetching, data: amcContract } = useRemainingCostInsuranceContract(
    card.id,
  );

  const patient = currentUser?.userPatientInformation?.userPatientProfiles?.find(
    p => p.isDefault,
  );
  const fullName = `${patient?.firstName} ${patient?.lastName}`;
  const amcImage = amcContract?.amcPicture?.thumbnailS3Id;
  const isBranded = amcContract?.isAmcBranded;

  const showRefundsInfo =
    codeList.findIndex(o => o.code === card.professional?.speciality?.code) !==
    -1;

  const refundsWording = React.useMemo(() => {
    if (amcContract) {
      if (
        amcContract.status === 'INCOMPATIBLE' ||
        amcContract.isAmcRemainingCostCompatible === false
      ) {
        return t('refunds-wording-amc-incompatible');
      }

      if (amcContract.status === 'NOT_VALIDATED') {
        return t('refunds-wording-amc-not-validated');
      }

      if (amcContract.status === 'VALIDATED') {
        return t('refunds-wording-amc-validated', {
          amcName: amcContract.amcName,
          userFullName: fullName,
        });
      }
    }
  }, [amcContract]);

  const onClickCardNoAmc = () => {
    if (currentUser) {
      // User is logged in: [current_path] => Add insurance page => [current_path]
      router.push({
        pathname: APP_ROUTES.ACCOUNT.ADD_INSURANCE,
        query: {
          back: router.asPath,
          next: router.asPath,
        },
      });
    } else {
      // User is not logged in: [current_path] => Login page => Add insurance page => [current_path]
      router.push({
        pathname: APP_ROUTES.LOGIN,
        query: {
          back: router.asPath,
          next: `${APP_ROUTES.ACCOUNT.ADD_INSURANCE}?next=${router.asPath}`,
        },
      });
    }
  };

  return (
    <CardSubSection
      title={t('refunds')}
      indicationIcon={
        <IconModalIndication
          title={t('refunds-help-title')}
          message={t('refunds-help-message')}
          iconColor={theme.palette.info.dark}
          iconSize="small"
          data-testid="refunds-help-icon"
          aria-label={t('accessibility-refunds-indications')}
          onOpen={() => {
            analyticsEvent({
              category: 'Tooltip',
              action: 'TooltipOpenClick',
              label: 'refunds-tooltip',
            });
          }}
        />
      }
    >
      {isFetching ? (
        <Skeleton height={60} />
      ) : (
        <>
          {!refundsWording ? (
            <Card
              sx={{
                display: 'flex',
                flexDirection: 'row',
                mt: 1,
                cursor: 'pointer',
              }}
              onClick={onClickCardNoAmc}
              aria-label={t('i-indicate-my-mutual')}
              title={t('i-indicate-my-mutual')}
            >
              <Box
                sx={{
                  bgcolor: 'background.xLightBlue',
                  px: theme.spacing(0.5),
                  py: theme.spacing(1),
                  paddingLeft: theme.spacing(0.25),
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  '& > svg': {
                    position: 'relative',
                    left: theme.spacing(1),
                  },
                }}
              >
                <IllustrationPersonWithFlag />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 2,
                }}
              >
                <Typography component="h3" variant="h6">
                  {t('i-indicate-my-mutual')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Typography sx={{ paddingRight: 1 }}>
                    {t('how-much-consultation-will-cost')}
                  </Typography>
                  <Box sx={{ color: 'primary.main' }}>
                    <IconChevronRight />
                  </Box>
                </Box>
              </Box>
            </Card>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center" pt={1}>
              {isBranded && amcImage && (
                <Box
                  component="img"
                  sx={{ maxWidth: '150px', maxHeight: '100px' }}
                  src={`/files/${amcImage}`}
                  aria-hidden="true"
                />
              )}
              <Typography>{refundsWording}</Typography>
            </Stack>
          )}
        </>
      )}

      {showRefundsInfo && (
        <Alert
          icon={<IconInfoRoundFilled />}
          severity="info"
          color="infoBlue"
          sx={{ mt: 2 }}
          data-testid="need-prescription-section"
        >
          <Trans
            i18nKey="refunds-info-message"
            components={{
              a: (
                <Link
                  key="ameli"
                  href="https://www.ameli.fr/assure/remboursements/etre-bien-rembourse/medecin-traitant-parcours-soins-coordonnes"
                  target="_blank"
                  sx={{ fontWeight: 'bold' }}
                />
              ),
            }}
          />
        </Alert>
      )}
    </CardSubSection>
  );
};
export default Refunds;
